import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { fetchPosts, adaptPost } from "@/lib/api";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
/**
 * Un lien de navigation qui ne recharge PAS la page.
 *
 * Tout le menu utilisait des <a href> bruts. Chaque clic sur Services, Projets
 * ou Blog declenchait un rechargement complet : nouveau HTML, JavaScript
 * re-analyse, application remontee de zero, donnees refetchees. D'ou la
 * sensation que le site met un temps fou a changer de page, alors qu'il s'agit
 * d'une application a page unique capable de basculer instantanement.
 *
 * Les ancres de l'accueil (#accueil, #contact) sont un cas a part : si on est
 * deja sur l'accueil, on defile ; sinon on navigue puis on defile.
 */
const LienNav = ({
  vers,
  className,
  onClick,
  children,
}: {
  vers: string;
  className?: string;
  onClick?: () => void;
  children: React.ReactNode;
}) => {
  const { pathname } = useLocation();
  const naviguer = useNavigate();
  const client = useQueryClient();
  const ancre = vers.startsWith("/#") ? vers.slice(2) : null;

  /**
   * Prechargement a l'intention.
   *
   * /blog est la seule page qui doit aller chercher ses donnees au reseau
   * avant de pouvoir s'afficher. Entre le moment ou le pointeur touche le lien
   * et le moment ou le doigt ou le clic arrive, il y a de quoi faire l'aller-
   * retour : la page est alors deja prete quand on y entre.
   * prefetchQuery ne refait rien si la donnee est deja fraiche.
   */
  const precharger = () => {
    if (vers !== "/blog") return;
    client.prefetchQuery({
      queryKey: ["posts"],
      queryFn: async () => (await fetchPosts()).map(adaptPost),
      staleTime: 5 * 60 * 1000,
    });
  };

  if (!ancre) {
    return (
      <Link
        to={vers}
        className={className}
        onClick={onClick}
        onMouseEnter={precharger}
        onTouchStart={precharger}
        onFocus={precharger}
      >
        {children}
      </Link>
    );
  }

  const aller = (e: React.MouseEvent) => {
    e.preventDefault();
    onClick?.();
    const cible = () => document.getElementById(ancre)?.scrollIntoView({ behavior: "smooth" });
    if (pathname === "/") cible();
    else {
      naviguer("/");
      // Un cadre d'animation laisse l'accueil se peindre avant de viser.
      requestAnimationFrame(() => requestAnimationFrame(cible));
    }
  };

  return (
    <a href={vers} className={className} onClick={aller}>
      {children}
    </a>
  );
};

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${isScrolled ? "top-3 left-3 right-3 bg-background/90 backdrop-blur-xl border border-border/50 shadow-elegant rounded-2xl" : "bg-transparent"}`}>
      <nav className="container mx-auto px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-primary rounded-full animate-glow"></div>
            <span className="text-lg sm:text-xl md:text-2xl font-bold text-foreground whitespace-nowrap">Tokam Darius</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <LienNav vers="/#accueil" className="text-muted-foreground hover:text-primary transition-colors duration-300">
              Accueil
            </LienNav>
            <LienNav vers="/services" className="text-muted-foreground hover:text-primary transition-colors duration-300">
              Services
            </LienNav>
            <LienNav vers="/projets" className="text-muted-foreground hover:text-primary transition-colors duration-300">
              Projets
            </LienNav>
            <LienNav vers="/blog" className="text-muted-foreground hover:text-primary transition-colors duration-300">
              Blog
            </LienNav>
            <LienNav vers="/#contact" className="text-muted-foreground hover:text-primary transition-colors duration-300">
              Contact
            </LienNav>
          </div>

          {/* Mobile Menu Button & Theme Toggle */}
          <div className="flex items-center gap-2">
            <div className="md:hidden">
              </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="md:hidden" 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 animate-fade-in bg-background/95 backdrop-blur-lg rounded-lg border border-border shadow-lg p-4">
            <div className="flex flex-col space-y-3">
              <LienNav vers="/#accueil" 
                className="text-muted-foreground hover:text-primary transition-colors duration-300 py-2 px-3 rounded-md hover:bg-muted/50" 
                onClick={() => setIsMenuOpen(false)}
              >
                Accueil
              </LienNav>
              <LienNav vers="/services" 
                className="text-muted-foreground hover:text-primary transition-colors duration-300 py-2 px-3 rounded-md hover:bg-muted/50" 
                onClick={() => setIsMenuOpen(false)}
              >
                Services
              </LienNav>
              <LienNav vers="/projets" 
                className="text-muted-foreground hover:text-primary transition-colors duration-300 py-2 px-3 rounded-md hover:bg-muted/50" 
                onClick={() => setIsMenuOpen(false)}
              >
                Projets
              </LienNav>
              <LienNav vers="/blog" 
                className="text-muted-foreground hover:text-primary transition-colors duration-300 py-2 px-3 rounded-md hover:bg-muted/50" 
                onClick={() => setIsMenuOpen(false)}
              >
                Blog
              </LienNav>
              <LienNav vers="/#contact" 
                className="text-muted-foreground hover:text-primary transition-colors duration-300 py-2 px-3 rounded-md hover:bg-muted/50" 
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </LienNav>
            </div>
          </div>
        )}
      </nav>
    </header>;
};